// MO7.1, MO7.1M
import React, { useCallback, useContext, useRef } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import {
  ThemeOptions,
  ThemeOverride,
} from '@wix/da-react-context/pkg/ThemeContext';
import { useBiLogger } from '@wix/da-bi/pkg/hooks/useBiLogger';
import { BiData, SearchClickBiEvent } from '@wix/da-bi/pkg/events';
import LozengeButtonOrLink from '@wix/da-ds/pkg/Lozenge/dsPresets/LozengeButtonOrLink';
import { useHeroImageForContainer } from '@wix/da-shared-react/pkg/utils/hooks/useHeroImageForContainer';
import { Section } from '@wix/da-a11y/pkg/components/headings/tagWrappers';
import Heading from '@wix/da-a11y/pkg/components/headings/Heading';
import { SubNavigation } from '../../../../../types/store';
import SearchBar from './SearchBar';
import { MeasuredCookie } from '@wix/da-shared-react/pkg/Measured/redux/MeasuredCookie';

import ArtAttribution from './ArtAttribution';
import s from './ShopPageHeader.scss';

// note: default bg image is set in scss
const DEFAULT_BG_ARTIST = {
  username: 'moxximonroe',
  usericon: 'https://a.deviantart.net/avatars-big/m/o/moxximonroe.jpg?4',
  userId: 46690854,
};

export interface Props {
  searchTerm?: string;
  bgDeviation?: PapiDeviation;
  quickSearches?: SubNavigation;
}

/** shop page explanation text and search input, sits above subnav tabs */
const ShopPageHeader: React.FC<Props> = ({
  searchTerm = '',
  bgDeviation,
  quickSearches,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const logBiEvent = useBiLogger();
  const isFirstVisit = useRef<boolean>(!MeasuredCookie.hasCookie());

  const submitCallback = useCallback(
    newSearchTerm => {
      logBiEvent(
        BiData<SearchClickBiEvent>({
          evid: 710,
          search_terms: newSearchTerm,
          sectionname: 'shop',
        })
      );

      window.location.assign(
        newSearchTerm
          ? getPathnameWithSearch(newSearchTerm)
          : getPathnameWithoutSearch()
      );
    },
    [logBiEvent]
  );

  const rootDivRef = useRef<HTMLDivElement>(null);
  const { hero } = useHeroImageForContainer(bgDeviation, rootDivRef, {
    isViewportWidth: true,
    width: 1024,
    height: 390,
    mobileWidth: 412,
    mobileHeight: 430,
  });
  const shouldUseBgImage =
    !!hero && !searchTerm && isFirstVisit.current === false;
  const backgroundImage = shouldUseBgImage ? `url(${hero.src})` : undefined;
  const shouldShowAttribution = !searchTerm && shouldUseBgImage;
  const bgAuthor =
    shouldUseBgImage && bgDeviation?.author
      ? bgDeviation?.author
      : DEFAULT_BG_ARTIST;

  return (
    <Section
      className={classnames(s['root'], searchTerm && s['with-search'])}
      ref={rootDivRef}
      style={{ backgroundImage }}
    >
      {shouldUseBgImage && (
        /* lets the preloader scan it */
        <img
          src={hero.src}
          alt=""
          width="0"
          height="0"
          style={{ display: 'none' }}
        />
      )}
      <ThemeOverride className={s['theme-override']} theme={ThemeOptions.LIGHT}>
        {!searchTerm && (
          <Heading className={s['title']}>{t('shopPage.header.title')}</Heading>
        )}

        <SearchBar
          className={s['search-bar']}
          id="shop-search-bar"
          name="q"
          placeholder={
            isMobile
              ? t('shopPage.header.searchFieldPlaceholder.mobile')
              : t('shopPage.header.searchFieldPlaceholder')
          }
          initialValue={searchTerm}
          buttonText={t('shopPage.header.searchButton')}
          onSubmit={submitCallback}
        />

        {!searchTerm && !!quickSearches?.options?.length && (
          <ul className={s['quick-search']}>
            <span className={s['quick-search-text']}>
              {t('shopPage.header.quickSearchText')}
            </span>
            <div className={s['quick-search-list']}>
              {quickSearches.options.map(({ subnav, url }) => (
                <LozengeButtonOrLink
                  key={subnav}
                  href={url}
                  biData={BiData<SearchClickBiEvent>({
                    evid: 710,
                    search_terms: subnav,
                    sectionname: 'shop_tag',
                  })}
                  className={s['quick-search-item']}
                >
                  {subnav}
                </LozengeButtonOrLink>
              ))}
            </div>
          </ul>
        )}

        {shouldShowAttribution && isMobile && (
          <ArtAttribution author={bgAuthor} />
        )}
      </ThemeOverride>

      {shouldShowAttribution && !isMobile && (
        <ArtAttribution author={bgAuthor} />
      )}
    </Section>
  );
};

ShopPageHeader.displayName = 'ShopPageHeader';
export default ShopPageHeader;

// these are to handle the special /shop/search1=term vs /shop
// others: /shop/commissions/search1=term vs. /shop/commissions
function getPathnameWithoutSearch(): string {
  if (typeof window === 'undefined') {
    return '/shop';
  }
  const { pathname: windowPathname, search } = window.location;
  const newPathname =
    windowPathname === '/shop/search'
      ? '/shop'
      : windowPathname.replace('/search', '');

  const params = new URLSearchParams(search);
  params.delete('q');
  const newLocationSearch = params.toString();
  return newLocationSearch
    ? `${newPathname}?${params.toString()}`
    : newPathname;
}

function getPathnameWithSearch(searchTerm: string): string {
  if (typeof window === 'undefined') {
    return '/shop/search';
  }
  const { pathname: windowPathname, search } = window.location;
  const newPathname = windowPathname.endsWith('/search')
    ? windowPathname
    : `${windowPathname}/search`;

  const params = new URLSearchParams(search);
  params.delete('q');
  params.append('q', searchTerm);
  return `${newPathname}?${params.toString()}`;
}
